import { Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from "../../../environments/environment";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";

export type Lang = 'fr' | 'en' | 'es' | 'key';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  public static readonly supportedLanguages: Lang[] = environment.supportedLanguages as Lang[];
  
  constructor(private readonly translate: TranslateService,
              private readonly auth0Service: AuthService
  ) {
  }
  
  get onLanguageChange(): Observable<LangChangeEvent> {
    return this.translate.onLangChange
  }
  
  get currentLanguage(): string {
    return this.translate.currentLang;
  }
  
  public initLanguage() {
    this.auth0Service.user$.subscribe({
      next: (user) => {
        if (user && user?.mp_locale) {
          // User?.mp_locale correspond à la "locale" stockée dans le user_metadata du user Auth0 rajouté ensuite à l'id token via le script post login
          this.translate.use(user?.mp_locale);
          return;
        }
        
        const browserLanguage = this.translate.getBrowserLang();
        if (this.isSupportedLanguage(browserLanguage)) {
          this.translate.use(browserLanguage);
          return;
        }
        
        this.translate.use(this.translate.getDefaultLang())
      },
      error: (err) => this.translate.use(this.translate.getDefaultLang())
    });
  }
  
  public use(language: Lang): void {
    this.translate.use(language);
  }
  
  public getUserLanguage(): Observable<string | null> {
    return this.auth0Service.user$.pipe(map(user => user?.mp_locale ?? null), take(1));
  }
  
  private isSupportedLanguage(lang: string | undefined | null): lang is Lang {
    return !!lang && LanguageService.supportedLanguages.includes(lang as Lang);
  }
}
